// O PDF da folha de laboratório da cabeceira — o mesmo desenho dos outros.
//
// A curva de cada marcador é desenhada com o canvas do pdfmake a partir da
// mesma geometria da tela (`geometriaDaCurva`). A meta é caber em UMA folha: é
// o papel que fica na cabeceira e que o médico lê em pé.
import { formatarData, formatarDataHora, formatarDiaMes } from '@medicov1/shared';
import { TINTA, MEIO, FRACO, LINHA, ALTO, OK, ATENCAO, FUNDO_CAIXA, titulo, comTitulo, baixarPdfDe } from './documentoPdf';
import { bloco } from './documentoPdfEspecial';
import { identificar } from './identificacao';
import {
  ROTULO_SITUACAO, geometriaDaCurva, dividirLeitura,
  type FolhaDeLaboratorioDTO, type LinhaDTO,
} from './relatorioLaboratorioTipos';

type Margem = [number, number, number, number];
const margem = (cima: number, direita: number, baixo: number, esquerda: number): Margem => [esquerda, cima, direita, baixo];

const AZUL = '#0e6f86';
const VERDE_FAIXA = '#e3f1e6';
const L = 200; // largura da curva no papel
const H = 18;

const corDa = (s: LinhaDTO['situacao']) => (s === 'alto' ? ALTO : s === 'baixo' ? AZUL : TINTA);
const SETA_PDF: Record<NonNullable<LinhaDTO['direcao']>, string> = { subiu: 'subiu', caiu: 'caiu', estavel: 'estável' };

function curva(l: LinhaDTO): unknown {
  const { pontos, faixa } = geometriaDaCurva(l.serie, l.referencia, L, H);
  const formas: unknown[] = [];
  if (faixa) formas.push({ type: 'rect', x: 0, y: faixa.y, w: L, h: faixa.h, color: VERDE_FAIXA });
  if (pontos.length > 1) formas.push({ type: 'polyline', points: pontos, lineWidth: 0.9, lineColor: '#52525b' });
  for (const p of pontos) formas.push({ type: 'ellipse', x: p.x, y: p.y, r1: 0.9, r2: 0.9, color: '#52525b' });
  const u = pontos[pontos.length - 1];
  if (u) formas.push({ type: 'ellipse', x: u.x, y: u.y, r1: 2, r2: 2, color: corDa(l.situacao) });
  return { canvas: formas, margin: margem(1, 0, 0, 2) };
}

function linhaDaTabela(l: LinhaDTO): unknown[] {
  const sit = l.situacao !== 'dentro' ? ROTULO_SITUACAO[l.situacao] : '';
  return [
    {
      stack: [
        { text: l.rotulo, bold: true, fontSize: 7.8, color: TINTA },
        ...(l.referencia ? [{ text: `${l.referencia.min.toLocaleString('pt-BR')}–${l.referencia.max.toLocaleString('pt-BR')} ${l.unidade}`, fontSize: 6, color: FRACO }] : []),
      ],
    },
    {
      stack: [
        { text: [{ text: l.ultimo.texto, bold: true, color: corDa(l.situacao) }, ...(sit ? [{ text: `  ${sit}`, fontSize: 6.2, color: corDa(l.situacao) }] : [])], fontSize: 8.6 },
        { text: `${formatarDiaMes(new Date(l.ultimo.em))}${l.diasDesde > 3 ? ` · há ${l.diasDesde} d` : ''}${l.direcao ? ` · ${SETA_PDF[l.direcao]}` : ''}`, fontSize: 6, color: FRACO },
      ],
    },
    l.anterior
      ? { stack: [{ text: l.anterior.texto, fontSize: 7.6, color: MEIO }, { text: formatarDiaMes(new Date(l.anterior.em)), fontSize: 6, color: FRACO }] }
      : { text: '—', color: LINHA },
    curva(l),
  ];
}

/** Monta a definição do pdfmake. Pura: testável fora do navegador. */
export function montarDocumentoLaboratorio(dados: FolhaDeLaboratorioDTO): unknown {
  const quem = identificar(dados.paciente, true);
  const conteudo: unknown[] = [];
  let n = 0;
  const leitura = dados.leitura ? dividirLeitura(dados.leitura.texto) : null;

  conteudo.push({
    columns: [
      {
        width: '*',
        stack: [
          { text: quem.nome, fontSize: 17, color: TINTA },
          { text: quem.linha, fontSize: 8, color: FRACO },
        ],
      },
      {
        width: 'auto',
        stack: [
          { text: 'EXAMES DE LABORATÓRIO', fontSize: 8.6, bold: true, characterSpacing: 1.3, alignment: 'right', color: TINTA },
          { text: dados.dia ? `coleta de ${formatarData(dados.dia)}` : `números de ${formatarDataHora(new Date(dados.gerado.em))}`, fontSize: 7, color: FRACO, alignment: 'right', margin: margem(2, 0, 0, 0) },
          ...(dados.leitura ? [{ text: `leitura de ${formatarDataHora(new Date(dados.leitura.geradoEm))}`, fontSize: 7, color: FRACO, alignment: 'right' }] : []),
        ],
      },
    ],
    margin: margem(0, 0, 6, 0),
  });
  conteudo.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.1, lineColor: TINTA }], margin: margem(0, 0, 7, 0) });

  // O resumo vem antes de tudo: é o que o médico lê se ler só uma coisa.
  if (leitura?.resumo) {
    conteudo.push({
      margin: margem(0, 0, 6, 0),
      table: {
        widths: [3, '*'],
        body: [[
          { text: '', fillColor: OK },
          {
            stack: [{ text: 'EM RESUMO', fontSize: 6.8, bold: true, characterSpacing: 1, color: OK, margin: margem(0, 0, 2, 0) }, bloco(leitura.resumo)],
            fillColor: FUNDO_CAIXA, margin: margem(5, 7, 1, 7),
          },
        ]],
      },
      layout: 'noBorders',
    });
  }

  // ------------------------------------------------------------ a tabela
  const corpo: unknown[][] = [[
    { text: 'EXAME', fontSize: 6, bold: true, color: FRACO, characterSpacing: 0.6 },
    { text: dados.dia ? 'NO DIA' : 'ÚLTIMO', fontSize: 6, bold: true, color: FRACO, characterSpacing: 0.6 },
    { text: 'ANTES', fontSize: 6, bold: true, color: FRACO, characterSpacing: 0.6 },
    { text: 'CURVA · 3 SEMANAS · FAIXA VERDE = NORMAL', fontSize: 6, bold: true, color: FRACO, characterSpacing: 0.6 },
  ]];
  for (const g of dados.grupos) {
    corpo.push([{ text: g.rotulo.toUpperCase(), colSpan: 4, fontSize: 6.4, bold: true, color: TINTA, characterSpacing: 0.8, fillColor: '#f4f4f5', margin: margem(1, 0, 1, 2) }, {}, {}, {}]);
    for (const l of g.linhas) corpo.push(linhaDaTabela(l));
  }
  // A tabela pode continuar na página seguinte (com o cabeçalho repetido): presa
  // num bloco só, com o resumo do dia em cima ela pulava inteira para a página 2
  // e deixava a primeira em branco (02/10). O título fica com o cabeçalho e a
  // primeira linha.
  conteudo.push(
    titulo(++n, dados.dia ? `Exames de ${formatarData(dados.dia)}` : 'Como estão'),
    {
      margin: margem(2, 0, 4, 0),
      table: { headerRows: 1, keepWithHeaderRows: 1, dontBreakRows: true, widths: [125, 92, 62, '*'], body: corpo },
      layout: {
        hLineWidth: (i: number) => (i === 1 ? 0.8 : 0.3),
        vLineWidth: () => 0,
        hLineColor: (i: number) => (i === 1 ? TINTA : LINHA),
        paddingLeft: () => 2, paddingRight: () => 4, paddingTop: () => 2.4, paddingBottom: () => 2.4,
      },
    },
  );

  // ------------------------------------------------------------ culturas
  // Até 30/09 só as sem resultado entravam (em "Vigiar"): a urocultura com
  // KPC + NDM sumiu do papel no dia em que saiu. As recentes saem inteiras —
  // e as que pedem atenção, com a explicação —; as antigas, numa linha só,
  // para a folha continuar cabendo em pé ao lado da cama.
  const culturas = dados.culturas ?? [];
  if (culturas.length > 0) {
    const corte = new Date(new Date(dados.gerado.em).getTime() - 30 * 86_400_000).toISOString().slice(0, 10);
    const recentes = culturas.filter((c) => c.coletadoEm >= corte);
    const antigas = culturas.filter((c) => c.coletadoEm < corte);
    const itens: unknown[] = recentes.map((c) => ({
      margin: margem(0, 0, 3, 0),
      stack: [
        { text: [
          { text: `${c.material} · colhida em ${formatarDiaMes(new Date(`${c.coletadoEm}T12:00:00`))} — `, bold: true },
          { text: c.situacao, bold: true, color: c.peso === 'atencao' ? ALTO : c.peso === 'tranquilizador' ? OK : MEIO },
        ], fontSize: 7.8 },
        ...(c.resultado ? [{ text: c.resultado, fontSize: 7.4, color: TINTA, lineHeight: 1.2 }] : []),
        ...(c.peso === 'atencao'
          ? [{ text: `${c.explicacao} Pergunta: ${c.pergunta}`, fontSize: 7, color: MEIO, lineHeight: 1.2, margin: margem(1, 0, 0, 0) }]
          : []),
      ],
    }));
    if (antigas.length > 0) {
      itens.push({
        text: `Antes: ${antigas.map((c) => `${c.material} ${formatarDiaMes(new Date(`${c.coletadoEm}T12:00:00`))} — ${c.resultado ?? 'sem resultado registrado'}`).join('; ')}.`,
        fontSize: 6.8, color: FRACO, lineHeight: 1.2,
      });
    }
    conteudo.push(...comTitulo(titulo(++n, 'Culturas'), itens[0]!, itens.slice(1)));
  }

  // ------------------------------------------------------------ vigiar
  if (dados.vigiar.length > 0) {
    conteudo.push(...comTitulo(
      titulo(++n, 'Vigiar'),
      {
        margin: margem(2, 0, 4, 0),
        ul: dados.vigiar.map((v) => ({
          text: v.texto, fontSize: 7.8, lineHeight: 1.25, margin: margem(0, 0, 1.5, 0),
          color: v.tipo === 'remedio' ? ALTO : v.tipo === 'fora' ? ATENCAO : MEIO,
        })),
      },
    ));
  }

  // ------------------------------------------------------------ leitura
  if (leitura && leitura.resto.length > 0) {
    const blocos = leitura.resto.map(bloco);
    conteudo.push(...comTitulo(titulo(++n, 'Leitura do conjunto', `${dados.leitura!.autor} · ${formatarData(new Date(dados.leitura!.geradoEm))}`), blocos[0]!, blocos.slice(1)));
  }

  return {
    pageSize: 'A4',
    pageMargins: [36, 34, 36, 38],
    defaultStyle: { font: 'Roboto', fontSize: 8, color: TINTA },
    info: { title: 'Exames de laboratório', author: 'saudev1' },
    footer: (pagina: number, total: number) => ({
      columns: [
        { text: 'saudev1 · registro de apoio da família, a partir dos laudos do hospital · não é diagnóstico', fontSize: 6.2, color: FRACO },
        { text: `${pagina} / ${total}`, fontSize: 6.2, color: FRACO, alignment: 'right' },
      ],
      margin: margem(0, 36, 0, 36),
    }),
    content: conteudo,
  };
}

export async function gerarPdfLaboratorio(dados: FolhaDeLaboratorioDTO): Promise<void> {
  const data = formatarData(new Date(dados.gerado.em)).replace(/\//g, '-');
  await baixarPdfDe(montarDocumentoLaboratorio(dados), `exames-laboratorio-${data}.pdf`);
}
